/*
 * Cart hold countdown.
 *
 * The cart drawer and the cart page replace their whole innerHTML on every cart
 * update, so this element is destroyed and rebuilt constantly. All of its state
 * therefore lives in localStorage and is re-read on each connect — never in the
 * instance itself.
 *
 * Rules:
 * - the cart gains an item  -> restart at the configured duration
 * - the cart shrinks        -> keep the running deadline
 * - the cart becomes empty  -> drop the deadline and hide
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'theme:cart-hold-timer';

  function readState() {
    try {
      const state = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      if (!state || typeof state.deadline !== 'number' || typeof state.count !== 'number') return null;
      return state;
    }
    catch (error) {
      return null;
    }
  }

  function writeState(state) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
    catch (error) {
      // Private browsing or a full quota: the countdown simply restarts.
    }
  }

  function clearState() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    catch (error) {
      // Nothing to recover from here.
    }
  }

  // Deadline to display for `count` items, or null when the cart is empty.
  function resolveDeadline(count, durationMs) {
    if (!count || count < 1) {
      clearState();
      return null;
    }

    const previous = readState();
    const deadline = previous && count <= previous.count
      ? previous.deadline
      : Date.now() + durationMs;

    writeState({ deadline, count });
    return deadline;
  }

  function formatRemaining(seconds) {
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return `${minutes < 10 ? '0' : ''}${minutes}:${rest < 10 ? '0' : ''}${rest}`;
  }

  class CartHoldTimer extends HTMLElement {
    constructor() {
      super();

      this.interval = null;
      this.deadline = null;
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleStorage = this.handleStorage.bind(this);
    }

    connectedCallback() {
      this.duration = (parseInt(this.getAttribute('data-duration'), 10) || 900) * 1000;
      this.clock = this.querySelector('[data-clock]');
      this.runningView = this.querySelector('[data-running]');
      this.expiredView = this.querySelector('[data-expired]');

      this.apply(parseInt(this.getAttribute('data-item-count'), 10) || 0);

      if (window.theme && theme.pubsub) {
        this.unsubscribe = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
      }
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('storage', this.handleStorage);
    }

    disconnectedCallback() {
      this.stop();

      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('storage', this.handleStorage);
    }

    handleCartUpdate(event) {
      if (!event || !event.cart || typeof event.cart.item_count !== 'number') return;

      this.apply(event.cart.item_count);
    }

    // Background tabs throttle setInterval, so recompute as soon as we come back.
    handleVisibilityChange() {
      if (!document.hidden) this.tick();
    }

    // Another tab changed the cart: follow its deadline instead of keeping ours.
    handleStorage(event) {
      if (event.key !== STORAGE_KEY) return;

      const state = readState();
      this.deadline = state ? state.deadline : null;

      if (this.deadline === null) {
        this.stop();
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.tick();
      this.start();
    }

    apply(count) {
      this.deadline = resolveDeadline(count, this.duration);

      if (this.deadline === null) {
        this.stop();
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.tick();
      this.start();
    }

    start() {
      this.stop();
      this.interval = window.setInterval(this.tick.bind(this), 1000);
    }

    stop() {
      if (this.interval === null) return;

      window.clearInterval(this.interval);
      this.interval = null;
    }

    // Always recomputed from the deadline, so the clock cannot drift.
    tick() {
      if (this.deadline === null) return;

      const remaining = Math.max(0, Math.round((this.deadline - Date.now()) / 1000));

      if (remaining === 0) {
        this.stop();
        this.setExpired(true);
        return;
      }

      this.setExpired(false);

      if (this.clock) {
        const formatted = formatRemaining(remaining);
        if (this.clock.textContent !== formatted) this.clock.textContent = formatted;
        this.clock.setAttribute('datetime', `PT${remaining}S`);
      }
    }

    setExpired(expired) {
      if (this.runningView) this.runningView.hidden = expired;
      if (this.expiredView) this.expiredView.hidden = !expired;

      this.toggleAttribute('data-is-expired', expired);
    }
  }

  if (!window.customElements.get('cart-hold-timer')) {
    window.customElements.define('cart-hold-timer', CartHoldTimer);
  }
})();
